/**
 * Integrytas Group — SkeletonRows (common).
 *
 * Loading placeholder for list pages (P-08): shimmering bars sized to the
 * grid's real row height so the layout doesn't jump when data lands.
 * Render while the FIRST fetch is in flight; subsequent refreshes should
 * keep the stale rows visible instead (no skeleton flash on refresh).
 *
 * Respects prefers-reduced-motion (the shimmer freezes to a flat bar).
 *
 * Usage:
 *   {loading && rows.length === 0
 *     ? <SkeletonRows rows={8} />
 *     : <table>…</table>}
 */

interface SkeletonRowsProps {
  /** Number of placeholder rows. Default 8. */
  rows?: number;
  /** Row height in px — match the grid's density row height. Default 28. */
  row_height?: number;
}

export function SkeletonRows({ rows = 8, row_height = 28 }: SkeletonRowsProps) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="w-full">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-csm-border-soft px-2"
          style={{ height: row_height }}
        >
          <div className="csm-skeleton h-3" style={{ width: `${18 + ((i * 13) % 22)}%` }} />
          <div className="csm-skeleton h-3" style={{ width: `${24 + ((i * 7) % 30)}%` }} />
          <div className="csm-skeleton h-3" style={{ width: `${10 + ((i * 11) % 14)}%` }} />
        </div>
      ))}
    </div>
  );
}
